import React, { useEffect, useState } from 'react';
import api from '../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, ExternalLink, FileWarning, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable modal to preview a bill INLINE (image / PDF / other) without downloading.
 *
 * Props:
 *  - open, onOpenChange: standard controlled dialog
 *  - expenditureId: string  (used with default apiPath)
 *  - apiPath: string  (optional — override the endpoint, e.g. `/branch-ledger/entries/${id}/bill`)
 *  - filename: string (optional label)
 */
const ViewBillModal = ({ open, onOpenChange, expenditureId, apiPath, filename }) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  const [error, setError] = useState(null);

  const endpoint = apiPath || (expenditureId ? `/expenditures/${expenditureId}/bill` : null);

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;

    if (open && endpoint) {
      setLoading(true);
      setError(null);
      api
        .get(endpoint, {
          params: { inline: true },
          responseType: 'blob',
        })
        .then((resp) => {
          if (cancelled) return;
          const ct = resp.headers?.['content-type'] || resp.data.type || 'application/octet-stream';
          const blob = new Blob([resp.data], { type: ct });
          currentUrl = URL.createObjectURL(blob);
          setBlobUrl(currentUrl);
          setContentType(ct);
        })
        .catch(() => {
          if (!cancelled) setError('Unable to load bill');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setBlobUrl(null);
      setContentType('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, endpoint]);

  const downloadFile = async () => {
    if (!endpoint) return;
    try {
      const resp = await api.get(endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'bill';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Bill downloaded');
    } catch {
      toast.error('Failed to download');
    }
  };

  const openInNewTab = () => {
    if (blobUrl) window.open(blobUrl, '_blank', 'noopener');
  };

  const isImage = contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span className="truncate">Bill / Voucher {filename ? `— ${filename}` : ''}</span>
            <div className="flex gap-2">
              {blobUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openInNewTab}
                  data-testid="view-bill-newtab"
                  className="h-8"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" />
                  New Tab
                </Button>
              )}
              <Button
                size="sm"
                onClick={downloadFile}
                className="h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="view-bill-download"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Download
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-slate-100 rounded-lg mt-2 min-h-[400px] flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">Loading bill…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 text-red-600 p-6 text-center">
              <FileWarning className="w-10 h-10" />
              <span className="text-sm">{error}</span>
            </div>
          ) : blobUrl ? (
            isImage ? (
              <img
                src={blobUrl}
                alt="Bill preview"
                className="max-w-full max-h-[70vh] object-contain"
                data-testid="bill-image"
              />
            ) : isPdf ? (
              <iframe
                src={blobUrl}
                title="Bill PDF preview"
                className="w-full h-[70vh] border-0 bg-white"
                data-testid="bill-pdf"
              />
            ) : (
              <div className="flex flex-col items-center gap-3 p-8 text-center text-slate-600">
                <FileWarning className="w-10 h-10 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-800">Preview not available for this file type</p>
                  <p className="text-xs text-slate-500 mt-1">{contentType || 'Unknown format'}</p>
                </div>
                <Button
                  onClick={downloadFile}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download to view
                </Button>
              </div>
            )
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewBillModal;
